import { createContext, useContext, useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "vantapulse_lang";

// Dictionnaire volontairement centré sur le chrome du site (Navbar, Sidebar, Paramètres) :
// c'est ce qui apparaît sur toutes les pages et ce qu'on veut voir changer immédiatement
// en basculant la langue. Le reste du site (contenu produit, pages marketing) reste en
// français pour le moment et peut être étendu ici au besoin.
const DICT = {
  fr: {
    nav_dashboard: "Dashboard",
    nav_settings: "Paramètres",
    nav_admin: "Admin",
    nav_logout: "Déconnexion",
    nav_login: "Connexion",
    nav_notifications: "Notifications",
    nav_no_notifications: "Aucune notification",
    nav_see_more: "Aller voir",

    sidebar_shop: "Boutique",
    sidebar_compare: "Comparer les produits",
    sidebar_pricing: "Tarifs",
    sidebar_info: "Info",
    sidebar_tutorial: "Créer son bot Discord",
    sidebar_support: "Support",
    sidebar_status: "Statut",
    sidebar_settings: "Paramètres",
    sidebar_payment_methods: "Moyens de paiement",
    sidebar_tagline: "Bots Discord & scripts FiveM",
    sidebar_section_explore: "Explorer",
    sidebar_section_help: "Aide",
    sidebar_shop_desc: "Tous nos produits",
    sidebar_pricing_desc: "Offres et durées",
    sidebar_compare_desc: "Côte à côte",
    sidebar_ai_desc: "Ton assistant IA",
    sidebar_discord_desc: "Rejoindre la communauté",
    sidebar_support_desc: "Tickets et assistance",
    sidebar_status_desc: "État des services",
    sidebar_status_ok: "Opérationnel",
    sidebar_status_warn: "Maintenance",
    sidebar_status_down: "Incident",
    sidebar_guest_title: "Bienvenue sur VantaPulse",
    sidebar_guest_text: "Connecte-toi pour acheter et gérer tes produits.",
    sidebar_guest_cta: "Se connecter",
    sidebar_open_dashboard: "Ouvrir mon espace",

    settings_title: "Paramètres",
    settings_tab_profile: "Profil",
    settings_tab_account: "Compte",
    settings_tab_security: "Sécurité",
    settings_tab_referral: "Parrainage",
    settings_tab_preferences: "Préférences",
    settings_language_title: "Langue du site",
    settings_language_desc: "Choisissez la langue affichée sur le site (navigation, paramètres).",
    settings_desc_profile: "Avatar et nom d'utilisateur",
    settings_desc_account: "Téléphone, Discord, mot de passe",
    settings_desc_security: "A2F, appareils connectés",
    settings_desc_referral: "Votre lien et vos récompenses",
    settings_desc_preferences: "Langue, rester connecté",
    settings_edit_profile: "Modifier le profil",
    settings_2fa_on: "A2F activée",
    settings_2fa_off: "A2F à activer",
    settings_tab_billing: "Facturation",
    settings_desc_billing: "Abonnements, factures, transfert",
    settings_tab_support: "Aide",
    settings_desc_support: "Créer / connecter un bot",
    settings_support_title: "Aide",
    settings_support_subtitle: "Le guide pour créer et connecter ton bot. Support, statut et Discord sont dans le menu ☰.",
    settings_support_status: "Statut des services",
    settings_support_status_desc: "Voir l'état des services en temps réel",
    settings_support_contact: "Contacter le support",
    settings_support_contact_desc: "Ouvrir un ticket ou poser une question",
    settings_support_tutorial: "Créer / connecter un bot",
    settings_support_tutorial_desc: "Le guide pas à pas pour bien démarrer",
    settings_support_discord: "Rejoindre le Discord",
    settings_support_discord_desc: "Communauté, entraide et annonces",
    lang_fr: "Français",
    lang_en: "English",
  },
  en: {
    nav_dashboard: "Dashboard",
    nav_settings: "Settings",
    nav_admin: "Admin",
    nav_logout: "Log out",
    nav_login: "Log in",
    nav_notifications: "Notifications",
    nav_no_notifications: "No notifications",
    nav_see_more: "See more",

    sidebar_shop: "Shop",
    sidebar_compare: "Compare products",
    sidebar_pricing: "Pricing",
    sidebar_info: "Info",
    sidebar_tutorial: "Create your Discord bot",
    sidebar_support: "Support",
    sidebar_status: "Status",
    sidebar_settings: "Settings",
    sidebar_payment_methods: "Payment methods",
    sidebar_tagline: "Discord bots & FiveM scripts",
    sidebar_section_explore: "Explore",
    sidebar_section_help: "Help",
    sidebar_shop_desc: "All our products",
    sidebar_pricing_desc: "Plans and durations",
    sidebar_compare_desc: "Side by side",
    sidebar_ai_desc: "Your AI assistant",
    sidebar_discord_desc: "Join the community",
    sidebar_support_desc: "Tickets and assistance",
    sidebar_status_desc: "Service health",
    sidebar_status_ok: "Operational",
    sidebar_status_warn: "Maintenance",
    sidebar_status_down: "Incident",
    sidebar_guest_title: "Welcome to VantaPulse",
    sidebar_guest_text: "Log in to buy and manage your products.",
    sidebar_guest_cta: "Log in",
    sidebar_open_dashboard: "Open my space",

    settings_title: "Settings",
    settings_tab_profile: "Profile",
    settings_tab_account: "Account",
    settings_tab_security: "Security",
    settings_tab_referral: "Referrals",
    settings_tab_preferences: "Preferences",
    settings_language_title: "Site language",
    settings_language_desc: "Choose the language shown across the site (navigation, settings).",
    settings_desc_profile: "Avatar and username",
    settings_desc_account: "Phone, Discord, password",
    settings_desc_security: "2FA, signed-in devices",
    settings_desc_referral: "Your link and your rewards",
    settings_desc_preferences: "Language, stay signed in",
    settings_edit_profile: "Edit profile",
    settings_2fa_on: "2FA on",
    settings_2fa_off: "2FA off",
    settings_tab_billing: "Billing",
    settings_desc_billing: "Subscriptions, invoices, transfer",
    settings_tab_support: "Help",
    settings_desc_support: "Create / connect a bot",
    settings_support_title: "Help",
    settings_support_subtitle: "The guide to create and connect your bot. Support, status and Discord are in the ☰ menu.",
    settings_support_status: "Service status",
    settings_support_status_desc: "Check live service status",
    settings_support_contact: "Contact support",
    settings_support_contact_desc: "Open a ticket or ask a question",
    settings_support_tutorial: "Create / connect a bot",
    settings_support_tutorial_desc: "The step-by-step guide to get started",
    settings_support_discord: "Join the Discord",
    settings_support_discord_desc: "Community, help and announcements",
    lang_fr: "Français",
    lang_en: "English",
  },
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "fr";
    } catch {
      return "fr";
    }
  });

  const setLang = useCallback((next) => {
    setLangState(next === "en" ? "en" : "fr");
    try {
      localStorage.setItem(STORAGE_KEY, next === "en" ? "en" : "fr");
    } catch { /* ignore */ }
  }, []);

  const t = useCallback((key) => (DICT[lang] && DICT[lang][key]) || DICT.fr[key] || key, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within a LanguageProvider");
  return ctx;
}
